import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo } from "react";

import { inject } from "../../lib/itn-ui-kit/services/service-container";

const useSseConnection = ({
  accessToken,
  manageConnection = true,
  onConnectionError,
  onRegisterStopper,
}) => {
  const sseService = useMemo(() => inject("sse-service"), []);

  useEffect(() => {
    if (typeof onRegisterStopper === "function") {
      onRegisterStopper(() => sseService.stop());
      return () => {
        onRegisterStopper(() => {});
      };
    }
  }, [onRegisterStopper, sseService]);

  useEffect(() => {
    if (!manageConnection) {
      return undefined;
    }

    if (!accessToken) {
      sseService.stop();
      return;
    }

    let isActive = true;

    (async () => {
      try {
        await sseService.start({ accessToken });
      } catch (error) {
        if (!isActive) {
          return;
        }
        Sentry.captureException(error, {
          tags: { scope: "sse-connection", stage: "start" },
        });
        if (typeof onConnectionError === "function") {
          onConnectionError("Unable to connect to live updates. Please refresh the page.");
        }
      }
    })();

    return () => {
      isActive = false;
      sseService.stop();
    };
  }, [accessToken, manageConnection, onConnectionError, sseService]);

  const addEventListener = useCallback(
    (eventName, handler) => sseService.on(eventName, handler),
    [sseService]
  );

  return {
    addEventListener,
    stop: useCallback(() => sseService.stop(), [sseService]),
    service: sseService,
  };
};

export default useSseConnection;
